export const PLAN_VIEWS = [
  "roadmap",
  "requirements",
  "ap",
  "transfer",
  "record",
  "gpa",
] as const;

export type PlanView = (typeof PLAN_VIEWS)[number];

export const PLANNING_CONTEXT_KEYS = [
  "planId",
  "importDegreeId",
  "addCourse",
  "course",
  "auditFixture",
] as const;

type PlanningSearchParams = Pick<URLSearchParams, "getAll">;

const HANDOFF_PATHS: Record<PlanView, string> = {
  roadmap: "/degree-planning",
  requirements: "/prerequisite-checker",
  ap: "/prerequisite-checker?view=ap-credits",
  transfer: "/degree-planning?transfer=1",
  record: "/profile",
  gpa: "/gpa-calculator",
};

export function resolvePlanView(value: string | null | undefined): PlanView {
  return PLAN_VIEWS.includes(value as PlanView) ? (value as PlanView) : "roadmap";
}

function appendPlanningContext(
  target: URLSearchParams,
  source: PlanningSearchParams,
) {
  PLANNING_CONTEXT_KEYS.forEach((key) => {
    source.getAll(key).forEach((value) => target.append(key, value));
  });
}

export function buildPlanningHref(
  view: PlanView,
  source: PlanningSearchParams,
): string {
  const target = new URLSearchParams({ view });
  appendPlanningContext(target, source);
  return `/planning?${target.toString()}`;
}

/** Selecting a saved plan retains the active Planning tab, including Transfer. */
export function buildPlanningPlanSelectionHref(planId: string | number, source: PlanningSearchParams): string {
  const target = new URLSearchParams({ view: resolvePlanView(source.getAll('view')[0]) });
  appendPlanningContext(target, source);
  target.set('planId', String(planId));
  return `/planning?${target.toString()}`;
}

export function buildPlanningHandoff(
  view: PlanView,
  source: PlanningSearchParams,
): string {
  const [path, defaults] = HANDOFF_PATHS[view].split("?");
  const target = new URLSearchParams(defaults);
  if (view === "record") target.set("tab", "previous_classes");
  if (view === "ap") target.set("view", "ap-credits");
  if (view === "transfer") target.set("transfer", "1");
  appendPlanningContext(target, source);
  const finalQuery = target.toString();
  return finalQuery ? `${path}?${finalQuery}` : path;
}

export function activePlanningContext(
  source: PlanningSearchParams,
): Array<(typeof PLANNING_CONTEXT_KEYS)[number]> {
  return PLANNING_CONTEXT_KEYS.filter((key) => source.getAll(key).length > 0);
}
